import { useLayoutEffect, useRef, useState } from 'react';
import ScrollBar from '../scroll-bar';
import style from './style.module.scss';

export interface MessageBoxProps extends React.HTMLAttributes<HTMLDivElement> {
	textContent: string;
	compact?: boolean;
}

export default function ScrollableText({ textContent, compact, className, ...props }: MessageBoxProps) {
	const textRef = useRef<HTMLParagraphElement>(null);
	const [isScrollable, setIsScrollable] = useState(false);

	useLayoutEffect(() => {
		const el = textRef.current;
		if (!el) return;

		let cancelled = false;
		const checkOverflow = () => {
			if (!cancelled) setIsScrollable(el.scrollHeight > el.clientHeight + 1);
		};

		checkOverflow();

		// ResizeObserver only fires when the element's box changes; the box here is
		// bounded by flex, so when a script-specific web font (e.g. Noto Sans Armenian)
		// finishes loading the content grows taller without resizing the box. Re-check
		// after fonts settle and on the next frame so overflow is detected reliably.
		const raf = requestAnimationFrame(checkOverflow);
		if (document.fonts?.ready) {
			document.fonts.ready.then(checkOverflow);
		}

		const resizeObserver = new ResizeObserver(checkOverflow);
		resizeObserver.observe(el);

		return () => {
			cancelled = true;
			cancelAnimationFrame(raf);
			resizeObserver.disconnect();
		};
	}, [textContent]);

	const scrollTextUp = () => {
		if (textRef.current) {
			textRef.current.scrollTop -= 150;
		}
	};

	const scrollTextDown = () => {
		if (textRef.current) {
			textRef.current.scrollTop += 150;
		}
	};

	return (
		<article
			{...props}
			className={[style.scrollable, compact ? style['scrollable--compact'] : '', className].filter(Boolean).join(' ')}
		>
			<p
				ref={textRef}
				className={[style.text, isScrollable ? style['text--with-scrollbar'] : ''].filter(Boolean).join(' ')}
				dangerouslySetInnerHTML={{ __html: textContent }}
			></p>
			{isScrollable && <ScrollBar upAction={scrollTextUp} downAction={scrollTextDown} />}
		</article>
	);
}
